import { useCallback, useEffect, useState } from 'react';

import { useAndroidBackButton } from './useAndroidBackButton';

export interface FullscreenToggle {
  /** Expandido de verdad: pedido Y permitido. */
  fullscreen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

/**
 * Pantalla completa de un mapa (o de cualquier capa que tape la pantalla).
 * Nació en el mapa del recorrido (2026-09-01) y se sacó aquí para que el mapa
 * compartido (`MapComponent`) se comporte exactamente igual.
 *
 * Mientras está expandido:
 *  - **Escape** lo pliega. Se escucha en la fase de CAPTURA y no sigue: si la
 *    capa está dentro de un modal (el visor de fotos), el Escape del modal
 *    (`BaseModal`, en la fase de burbuja) no llega a cerrarlo también.
 *  - **«Atrás» de Android** lo pliega (pila de `useAndroidBackButton`: gana el
 *    último registrado, que es este).
 *  - **El scroll de la página se bloquea**: un dedo sobre el mapa movería
 *    contenido que no se ve. Se restaura el valor previo, no uno inventado.
 *
 * Con `allowed` a `false` nunca está expandido, aunque se hubiera pedido:
 * así no queda un estado atrapado si quien lo usa apaga la opción.
 */
export function useFullscreenToggle(allowed: boolean): FullscreenToggle {
  const [requested, setRequested] = useState(false);
  const fullscreen = allowed && requested;

  const open = useCallback(() => setRequested(true), []);
  const close = useCallback(() => setRequested(false), []);
  const toggle = useCallback(() => setRequested((previous) => !previous), []);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setRequested(false);
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [fullscreen]);

  useAndroidBackButton(fullscreen, close);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [fullscreen]);

  return { fullscreen, open, close, toggle };
}

export default useFullscreenToggle;
